import React, { useRef, useEffect, useState } from 'react';
import { usePDF } from '../context/PDFContext';
import { ZoomIn, ZoomOut, Undo, Redo, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MainCanvas = () => {
  const { 
    pages, zoom, setZoom, canUndo, canRedo, undo, redo, currentTool, pdfInfo, setPdfInfo, setPages, setIsProcessing,
    textElements, setTextElements, selectedTextId, setSelectedTextId, pendingText, setPendingText,
    imageElements, setImageElements, selectedImageId, setSelectedImageId, pendingImage, setPendingImage, pushUndo,
    highlightConfig
  } = usePDF();
  const [eraseRect, setEraseRect] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [draggingTextId, setDraggingTextId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [draggingImageId, setDraggingImageId] = useState(null);
  const [imageDragOffset, setImageDragOffset] = useState({ x: 0, y: 0 });
  const [highlightElements, setHighlightElements] = useState([]);
  const [resizingImageId, setResizingImageId] = useState(null);
  const resizeStart = useRef(null);
  const pageImgRefs = useRef({});
  const lastTouchPos = useRef({ x: 0, y: 0 });

  const handleZoomIn = () => {
    setZoom(Math.min(zoom + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(zoom - 0.25, 0.5));
  };

  const handleMouseDown = (e, pageNumber) => {
    if (!currentTool || !pdfInfo) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    if (currentTool === 'erase' || currentTool === 'highlight') {
      setIsDragging(true);
      setDragStart({ x: clickX, y: clickY });
      setCurrentPage(pageNumber);
      setEraseRect({ x: clickX, y: clickY, width: 0, height: 0, pageNumber });
    }
  };

  const handleMouseMove = (e, pageNumber) => {
    // Handle image/signature resizing via corner handle
    if (resizingImageId && resizeStart.current) {
      const dx = e.clientX - resizeStart.current.x;
      const newW = Math.max(20, resizeStart.current.w + dx);
      const newH = Math.max(10, newW * (resizeStart.current.h / resizeStart.current.w));
      setImageElements(prev => prev.map(img =>
        img.id === resizingImageId ? { ...img, width: newW, height: newH } : img
      ));
      return;
    }

    if ((currentTool === 'erase' || currentTool === 'highlight') && isDragging && pageNumber === currentPage) {
      const rect = e.currentTarget.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      const width = currentX - dragStart.x;
      const height = currentY - dragStart.y;

      setEraseRect({
        x: width < 0 ? currentX : dragStart.x,
        y: height < 0 ? currentY : dragStart.y,
        width: Math.abs(width),
        height: Math.abs(height),
        pageNumber,
      });
    }
    
    // Handle text dragging
    if (draggingTextId && currentTool === 'text') {
      const DESKTOP_DAMPING = 0.6;
      const deltaX = (e.clientX - dragOffset.lastX) * DESKTOP_DAMPING;
      const deltaY = (e.clientY - dragOffset.lastY) * DESKTOP_DAMPING;
      setDragOffset(prev => ({ ...prev, lastX: e.clientX, lastY: e.clientY }));
      
      setTextElements(prev => prev.map(text => 
        text.id === draggingTextId
          ? { ...text, x: text.x + deltaX, y: text.y + deltaY }
          : text
      ));
    }

    // Handle image dragging
    if (draggingImageId && (currentTool === 'image' || currentTool === 'signature')) {
      const DESKTOP_DAMPING = 0.6;
      const deltaX = (e.clientX - imageDragOffset.lastX) * DESKTOP_DAMPING;
      const deltaY = (e.clientY - imageDragOffset.lastY) * DESKTOP_DAMPING;
      setImageDragOffset(prev => ({ ...prev, lastX: e.clientX, lastY: e.clientY }));

      setImageElements(prev => prev.map(img =>
        img.id === draggingImageId
          ? { ...img, x: img.x + deltaX, y: img.y + deltaY }
          : img
      ));
    }
  };

  const handleMouseUp = async (e, pageNumber) => {
    if ((currentTool === 'erase' || currentTool === 'highlight') && isDragging) {
      const isHighlight = currentTool === 'highlight';
      setIsDragging(false);

      if (!eraseRect || eraseRect.width < 5 || eraseRect.height < 5) {
        setEraseRect(null);
        toast.error(`Selection too small. Drag to select an area to ${isHighlight ? 'highlight' : 'erase'}.`);
        return;
      }

      // Highlights are staged locally first so they can be removed before saving
      if (isHighlight) {
        setHighlightElements(prev => [...prev, {
          id: Date.now().toString(),
          pageNumber,
          x: eraseRect.x, y: eraseRect.y,
          width: eraseRect.width, height: eraseRect.height,
          color: highlightConfig.color, opacity: highlightConfig.opacity,
        }]);
        setEraseRect(null);
        toast.success('Highlight placed! Click × to remove it, or Save Highlights.');
        return;
      }

      const page = pages.find(p => p.page_number === pageNumber);
      if (!page) {
        setEraseRect(null);
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      const displayedWidth = rect.width;
      const pdfWidth = page.width || 612;
      const pdfHeight = page.height || 792;
      
      const scaleX = pdfWidth / displayedWidth;
      const scaleY = pdfHeight / rect.height;

      const pdfX = eraseRect.x * scaleX;
      const pdfY = eraseRect.y * scaleY;
      const pdfWidth_rect = eraseRect.width * scaleX;
      const pdfHeight_rect = eraseRect.height * scaleY;

      try {
        setIsProcessing(true);
        const formData = new FormData();
        formData.append('file_id', pdfInfo.id);
        formData.append('page_num', pageNumber);
        formData.append('x', pdfX);
        formData.append('y', pdfY);
        formData.append('width', pdfWidth_rect);
        formData.append('height', pdfHeight_rect);

        const response = await axios.post(`${API}/pdf/erase-text`, formData);
        setPdfInfo({ ...pdfInfo, id: response.data.id });

        const pagesResponse = await axios.get(`${API}/pdf/pages/${response.data.id}`);
        setPages(pagesResponse.data.pages);

        toast.success('Text erased from PDF!');
        setEraseRect(null);
      } catch (error) {
        console.error('Erase text error:', error);
        toast.error('Failed to erase text: ' + (error.response?.data?.detail || error.message));
      } finally {
        setIsProcessing(false);
      }
    }
    
    setDraggingTextId(null);
    setDraggingImageId(null);
    setResizingImageId(null);
  };

  const handleResizeMouseDown = (e, imageId) => {
    e.stopPropagation();
    const img = imageElements.find(i => i.id === imageId);
    if (!img) return;
    setSelectedImageId(imageId);
    setResizingImageId(imageId);
    resizeStart.current = { x: e.clientX, y: e.clientY, w: img.width, h: img.height };
  };

  const handleResizeTouchStart = (e, imageId) => {
    e.stopPropagation();
    const img = imageElements.find(i => i.id === imageId);
    if (!img) return;
    const touch = e.touches[0];
    setSelectedImageId(imageId);
    setResizingImageId(imageId);
    resizeStart.current = { x: touch.clientX, y: touch.clientY, w: img.width, h: img.height };
  };

  const handlePageClick = async (e, pageNumber) => {
    if (!currentTool || !pdfInfo || currentTool === 'erase' || currentTool === 'highlight') return;

    // Don't place if clicking on existing elements
    if (e.target.dataset.textElement || e.target.dataset.imageElement) return;

    // Deselect any selected text/image when clicking empty space
    if (selectedTextId) setSelectedTextId(null);
    if (selectedImageId) setSelectedImageId(null);

    if (currentTool === 'text' && pendingText) {
      const imgEl = pageImgRefs.current[pageNumber];
      if (!imgEl) return;
      const rect = imgEl.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // Store pdfFontSize at placement time so overlay matches saved PDF exactly
      const page = pages.find(p => p.page_number === pageNumber);
      const pdfWidth = page?.width || 612;
      const scale = pdfWidth / rect.width;

      const newTextElement = {
        id: Date.now().toString(),
        pageNumber,
        x: clickX,
        y: clickY,
        text: pendingText.text,
        fontSize: pendingText.fontSize,
        pdfFontSize: pendingText.fontSize * scale,
        color: pendingText.color,
        fontFamily: pendingText.fontFamily || 'helvetica',
      };

      setTextElements([...textElements, newTextElement]);
      setPendingText(null);
      toast.success('Text placed! Drag to reposition or click to edit.');
    }

    // Place image on click
    if ((currentTool === 'image' || currentTool === 'signature') && pendingImage) {
      const imgEl = pageImgRefs.current[pageNumber];
      if (!imgEl) return;
      const rect = imgEl.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // Convert pdfW/pdfH (PDF points) to display pixels so all four fields share one unit
      const page = pages.find(p => p.page_number === pageNumber);
      const scale = (page?.width || 612) / rect.width;

      pushUndo();
      const newImageElement = {
        id: Date.now().toString(),
        pageNumber,
        x: clickX,
        y: clickY,
        width: (pendingImage.pdfW || 150) / scale,
        height: (pendingImage.pdfH || 112) / scale,
        data: pendingImage.data,
      };

      setImageElements(prev => [...prev, newImageElement]);
      setPendingImage(null);
      toast.success('Image placed! Drag to reposition.');
    }
  };

  const handleTextMouseDown = (e, textId) => {
    e.stopPropagation();
    setSelectedTextId(textId);
    
    const text = textElements.find(t => t.id === textId);
    if (!text) return;
    
    setDraggingTextId(textId);
    setDragOffset({ x: 0, y: 0, lastX: e.clientX, lastY: e.clientY });
  };

  const handleTextTouchStart = (e, textId) => {
    e.stopPropagation();
    setSelectedTextId(textId);

    const text = textElements.find(t => t.id === textId);
    if (!text) return;

    const touch = e.touches[0];
    lastTouchPos.current = { x: touch.clientX, y: touch.clientY };
    setDraggingTextId(textId);
  };

  const PDF_FONT_ASCENDER = {
    'helvetica': 0.8467, 'arial': 0.8467,
    'helvetica-bold': 0.8467,
    'times': 0.8374, 'georgia': 0.8374,
    'times-bold': 0.8374,
    'courier': 0.7661,
    'inter': 0.8638, 'roboto': 0.8418, 'open-sans': 0.8879,
    'montserrat': 0.8585, 'lato': 0.8870, 'poppins': 0.8500,
    'lora': 0.8660, 'merriweather': 0.8555, 'playfair-display': 0.9155,
    'pt-serif': 0.8765, 'eb-garamond': 0.8545,
    'roboto-mono': 0.8884, 'fira-code': 0.8340, 'source-code-pro': 0.8555,
    'space-mono': 0.8795,
    'oswald': 0.9520, 'bebas-neue': 0.8000, 'anton': 0.9236, 'cinzel': 0.8020,
    'caveat': 0.8300, 'pacifico': 0.9250, 'dancing-script': 0.8200,
    'shadows-into-light': 0.8716,
  };

  const PDF_PREVIEW_FONT = {
    'helvetica': 'PDFHelvetica', 'arial': 'PDFHelvetica', 'helvetica-bold': 'PDFHelvetica',
    'times': 'PDFTimes', 'georgia': 'PDFTimes', 'times-bold': 'PDFTimes',
    'courier': 'PDFCourier',
    'inter': 'PDF-Inter', 'roboto': 'PDF-Roboto', 'open-sans': 'PDF-Open-Sans',
    'montserrat': 'PDF-Montserrat', 'lato': 'PDF-Lato', 'poppins': 'PDF-Poppins',
    'lora': 'PDF-Lora', 'merriweather': 'PDF-Merriweather', 'playfair-display': 'PDF-Playfair-Display',
    'pt-serif': 'PDF-PT-Serif', 'eb-garamond': 'PDF-EB-Garamond',
    'roboto-mono': 'PDF-Roboto-Mono', 'fira-code': 'PDF-Fira-Code', 'source-code-pro': 'PDF-Source-Code-Pro',
    'space-mono': 'PDF-Space-Mono',
    'oswald': 'PDF-Oswald', 'bebas-neue': 'PDF-Bebas-Neue', 'anton': 'PDF-Anton', 'cinzel': 'PDF-Cinzel',
    'caveat': 'PDF-Caveat', 'pacifico': 'PDF-Pacifico', 'dancing-script': 'PDF-Dancing-Script',
    'shadows-into-light': 'PDF-Shadows-Into-Light',
  };

  const saveTextToPDF = async () => {
    if (textElements.length === 0) {
      toast.error('No text to save');
      return;
    }

    setIsProcessing(true);
    let currentFileId = pdfInfo.id;

    try {
      for (const textEl of textElements) {
        const page = pages.find(p => p.page_number === textEl.pageNumber);
        if (!page) continue;

        const imgElement = pageImgRefs.current[textEl.pageNumber];
        if (!imgElement || !imgElement.clientWidth) continue;

        const imgRect = imgElement.getBoundingClientRect();
        const pdfWidth = page.width || 612;
        
        // Calculate scale from displayed image to PDF coordinates
        const scale = pdfWidth / imgRect.width;
        
        // Text x/y are already relative to the img element
        const pdfX = textEl.x * scale;
        const pdfFontSize = textEl.pdfFontSize || Math.round(textEl.fontSize * scale);

        const fontAscender = PDF_FONT_ASCENDER[(textEl.fontFamily || 'helvetica').toLowerCase()] || 0.8467;

        // Ensure the preview webfont is fully loaded before measuring (prevents fallback-font measurements)
        const previewFont = PDF_PREVIEW_FONT[(textEl.fontFamily || 'helvetica').toLowerCase()] || 'PDFHelvetica';
        try {
          await document.fonts.load(`${textEl.fontSize}px "${previewFont}"`);
          await document.fonts.ready;
        } catch {}

        // Exact baseline: zero-size inline-block marker sits on the rendered text baseline (pure DOM geometry)
        let baselinePx = null;
        const el = document.querySelector(`[data-testid="text-element-${textEl.id}"]`);
        if (el) {
          const marker = document.createElement('span');
          marker.style.cssText = 'display:inline-block;width:0;height:0;';
          el.appendChild(marker);
          baselinePx = marker.getBoundingClientRect().top - el.getBoundingClientRect().top;
          el.removeChild(marker);
        }
        const pdfY = baselinePx !== null
          ? (textEl.y + baselinePx) * scale
          : textEl.y * scale + pdfFontSize * fontAscender;

        const formData = new FormData();
        formData.append('file_id', currentFileId);
        formData.append('page_num', textEl.pageNumber);
        formData.append('x', pdfX);
        formData.append('y', pdfY);
        formData.append('text', textEl.text);
        formData.append('font_size', pdfFontSize);
        formData.append('color', textEl.color);
        formData.append('font_family', textEl.fontFamily || 'helvetica');
        if (localStorage.getItem('pdf_debug') === '1') formData.append('debug', 'true');

        const response = await axios.post(`${API}/pdf/add-text`, formData);
        currentFileId = response.data.id;
      }

      setPdfInfo({ ...pdfInfo, id: currentFileId });
      const pagesResponse = await axios.get(`${API}/pdf/pages/${currentFileId}`);
      setPages(pagesResponse.data.pages);

      setTextElements([]);
      setSelectedTextId(null);
      toast.success(`Saved ${textElements.length} text element(s) to PDF!`);
    } catch (error) {
      console.error('Save text error:', error?.response?.data || error);
      toast.error('Failed to save text: ' + (error?.response?.data?.detail || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Image handlers ---
  const handleImageMouseDown = (e, imageId) => {
    e.stopPropagation();
    setSelectedImageId(imageId);
    setDraggingImageId(imageId);
    setImageDragOffset({ lastX: e.clientX, lastY: e.clientY });
  };

  const handleImageTouchStart = (e, imageId) => {
    e.stopPropagation();
    setSelectedImageId(imageId);
    const touch = e.touches[0];
    lastTouchPos.current = { x: touch.clientX, y: touch.clientY };
    setDraggingImageId(imageId);
  };

  const saveImagesToPDF = async () => {
    if (imageElements.length === 0) {
      toast.error('No images to save');
      return;
    }

    setIsProcessing(true);
    let currentFileId = pdfInfo.id;

    try {
      for (const imgEl of imageElements) {
        const page = pages.find(p => p.page_number === imgEl.pageNumber);
        if (!page) continue;

        const imgElement = pageImgRefs.current[imgEl.pageNumber];
        if (!imgElement || !imgElement.clientWidth) continue;

        const imgRect = imgElement.getBoundingClientRect();
        const pdfWidth = page.width || 612;
        const scale = pdfWidth / imgRect.width;

        const pdfX = imgEl.x * scale;
        const pdfY = imgEl.y * scale;
        const pdfW = imgEl.width * scale;
        const pdfH = imgEl.height * scale;

        const formData = new FormData();
        formData.append('file_id', currentFileId);
        formData.append('page_num', imgEl.pageNumber);
        formData.append('x', pdfX);
        formData.append('y', pdfY);
        formData.append('width', pdfW);
        formData.append('height', pdfH);
        formData.append('image_data', imgEl.data);

        const response = await axios.post(`${API}/pdf/add-image`, formData);
        currentFileId = response.data.id;
      }

      setPdfInfo({ ...pdfInfo, id: currentFileId });
      const pagesResponse = await axios.get(`${API}/pdf/pages/${currentFileId}`);
      setPages(pagesResponse.data.pages);

      setImageElements([]);
      setSelectedImageId(null);
      toast.success(`Saved ${imageElements.length} image(s) to PDF!`);
    } catch (error) {
      console.error('Save image error:', error?.response?.data || error);
      toast.error('Failed to save images: ' + (error?.response?.data?.detail || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  const saveHighlightsToPDF = async () => {
    if (highlightElements.length === 0) {
      toast.error('No highlights to save');
      return;
    }

    setIsProcessing(true);
    let currentFileId = pdfInfo.id;

    try {
      for (const hl of highlightElements) {
        const page = pages.find(p => p.page_number === hl.pageNumber);
        if (!page) continue;

        const imgElement = pageImgRefs.current[hl.pageNumber];
        if (!imgElement || !imgElement.clientWidth) continue;

        const imgRect = imgElement.getBoundingClientRect();
        const scaleX = (page.width || 612) / imgRect.width;
        const scaleY = (page.height || 792) / imgRect.height;

        const formData = new FormData();
        formData.append('file_id', currentFileId);
        formData.append('page_num', hl.pageNumber);
        formData.append('x', hl.x * scaleX);
        formData.append('y', hl.y * scaleY);
        formData.append('width', hl.width * scaleX);
        formData.append('height', hl.height * scaleY);
        formData.append('color', hl.color);
        formData.append('opacity', hl.opacity);

        const response = await axios.post(`${API}/pdf/add-highlight`, formData);
        currentFileId = response.data.id;
      }

      setPdfInfo({ ...pdfInfo, id: currentFileId });
      const pagesResponse = await axios.get(`${API}/pdf/pages/${currentFileId}`);
      setPages(pagesResponse.data.pages);

      setHighlightElements([]);
      toast.success('Highlights saved to PDF!');
    } catch (error) {
      console.error('Save highlights error:', error?.response?.data || error);
      toast.error('Failed to save highlights: ' + (error?.response?.data?.detail || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  const getSpacing = () => {
    const baseSpacing = 24;
    return baseSpacing * zoom;
  };

  if (!pdfInfo || pages.length === 0) {
    return null;
  }

  return (
    <div className="flex-1 bg-background h-[calc(100vh-3.5rem)] flex flex-col">
      <div className="border-b border-border bg-surface px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            data-testid="undo-button"
            variant="outline"
            size="sm"
            className="h-9 w-9 p-0 rounded-md"
            onClick={undo}
            disabled={!canUndo}
          >
            <Undo className="w-4 h-4" />
          </Button>
          <Button
            data-testid="redo-button"
            variant="outline"
            size="sm"
            className="h-9 w-9 p-0 rounded-md"
            onClick={redo}
            disabled={!canRedo}
          >
            <Redo className="w-4 h-4" />
          </Button>
          
          {textElements.length > 0 && (
            <>
              <div className="h-6 w-px bg-border mx-1" />
              <Button
                data-testid="save-text-button"
                onClick={saveTextToPDF}
                size="sm"
                className="h-9 gap-2"
              >
                <Save className="w-4 h-4" />
                Save {textElements.length} Text
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0"
                onClick={() => {
                  setTextElements([]);
                  setSelectedTextId(null);
                  setPendingText(null);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </>
          )}

          {imageElements.length > 0 && (
            <>
              <div className="h-6 w-px bg-border mx-1" />
              <Button
                data-testid="save-images-button"
                onClick={saveImagesToPDF}
                size="sm"
                className="h-9 gap-2"
              >
                <Save className="w-4 h-4" />
                Save {imageElements.length} Image{imageElements.length > 1 ? 's' : ''}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0"
                onClick={() => {
                  setImageElements([]);
                  setSelectedImageId(null);
                  setPendingImage(null);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </>
          )}

          {highlightElements.length > 0 && (
            <>
              <div className="h-6 w-px bg-border mx-1" />
              <Button
                data-testid="save-highlights-button"
                onClick={saveHighlightsToPDF}
                size="sm"
                className="h-9 gap-2"
              >
                <Save className="w-4 h-4" />
                Save {highlightElements.length} Highlight{highlightElements.length > 1 ? 's' : ''}
              </Button>
              <Button
                data-testid="clear-highlights-button"
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0"
                onClick={() => setHighlightElements([])}
              >
                <X className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            data-testid="zoom-out-button"
            variant="outline"
            size="sm"
            className="h-9 w-9 p-0 rounded-md"
            onClick={handleZoomOut}
            disabled={zoom <= 0.5}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium min-w-[4rem] text-center" data-testid="zoom-level">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            data-testid="zoom-in-button"
            variant="outline"
            size="sm"
            className="h-9 w-9 p-0 rounded-md"
            onClick={handleZoomIn}
            disabled={zoom >= 3}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-8 flex justify-center" data-testid="pdf-canvas">
          <div style={{ display: 'flex', flexDirection: 'column', gap: `${getSpacing()}px` }}>
            {pages.map((page) => (
              <div
                key={page.page_number}
                data-testid={`pdf-page-${page.page_number}`}
                className="relative bg-white rounded shadow-xl"
                style={{
                  transformOrigin: 'top center',
                  cursor: currentTool === 'erase' || currentTool === 'highlight' ? 'crosshair' : (currentTool === 'text' && pendingText) || ((currentTool === 'image' || currentTool === 'signature') && pendingImage) ? 'pointer' : 'default',
                }}
                onMouseDown={(e) => handleMouseDown(e, page.page_number)}
                onMouseMove={(e) => handleMouseMove(e, page.page_number)}
                onMouseUp={(e) => handleMouseUp(e, page.page_number)}
                onClick={(e) => handlePageClick(e, page.page_number)}
                onTouchEnd={(e) => {
                  // End image resize on mobile
                  if (resizingImageId) {
                    setResizingImageId(null);
                    return;
                  }
                  // End text drag on mobile
                  if (draggingTextId) {
                    setDraggingTextId(null);
                    return;
                  }
                  // End image drag on mobile
                  if (draggingImageId) {
                    setDraggingImageId(null);
                    return;
                  }
                  // Place text on tap (mobile)
                  if (currentTool === 'text' && pendingText && !e.target.dataset.textElement) {
                    const touch = e.changedTouches[0];
                    if (touch) {
                      const imgEl = pageImgRefs.current[page.page_number];
                      if (imgEl) {
                        const rect = imgEl.getBoundingClientRect();
                        const clickX = touch.clientX - rect.left;
                        const clickY = touch.clientY - rect.top;
                        const pdfWidth = page.width || 612;
                        const scale = pdfWidth / rect.width;
                        setTextElements(prev => [...prev, {
                          id: Date.now().toString(),
                          pageNumber: page.page_number,
                          x: clickX,
                          y: clickY,
                          text: pendingText.text,
                          fontSize: pendingText.fontSize,
                          pdfFontSize: pendingText.fontSize * scale,
                          color: pendingText.color,
                          fontFamily: pendingText.fontFamily || 'helvetica',
                        }]);
                        setPendingText(null);
                        toast.success('Text placed! Drag to reposition or click to edit.');
                        return;
                      }
                    }
                  }
                  // Place image on tap (mobile)
                  if ((currentTool === 'image' || currentTool === 'signature') && pendingImage && !e.target.dataset.imageElement) {
                    const touch = e.changedTouches[0];
                    if (touch) {
                      const imgEl = pageImgRefs.current[page.page_number];
                      if (imgEl) {
                        const rect = imgEl.getBoundingClientRect();
                        const clickX = touch.clientX - rect.left;
                        const clickY = touch.clientY - rect.top;
                        // Convert pdfW/pdfH (PDF points) to display pixels so all four fields share one unit
                        const scale = (page.width || 612) / rect.width;
                        pushUndo();
                        setImageElements(prev => [...prev, {
                          id: Date.now().toString(),
                          pageNumber: page.page_number,
                          x: clickX,
                          y: clickY,
                          width: (pendingImage.pdfW || 150) / scale,
                          height: (pendingImage.pdfH || 112) / scale,
                          data: pendingImage.data,
                        }]);
                        setPendingImage(null);
                        toast.success('Image placed! Drag to reposition.');
                        return;
                      }
                    }
                  }
                  // Deselect when tapping empty space on mobile
                  if (!e.target.dataset.textElement && selectedTextId) {
                    setSelectedTextId(null);
                  }
                  if (!e.target.dataset.imageElement && selectedImageId) {
                    setSelectedImageId(null);
                  }
                }}
                onTouchMove={(e) => {
                  // Handle image/signature resizing on mobile
                  if (resizingImageId && resizeStart.current) {
                    e.preventDefault();
                    const touch = e.touches[0];
                    const dx = touch.clientX - resizeStart.current.x;
                    const newW = Math.max(20, resizeStart.current.w + dx);
                    const newH = Math.max(10, newW * (resizeStart.current.h / resizeStart.current.w));
                    setImageElements(prev => prev.map(img =>
                      img.id === resizingImageId ? { ...img, width: newW, height: newH } : img
                    ));
                    return;
                  }
                  // Handle text dragging on mobile with damping for precision
                  if (draggingTextId && currentTool === 'text') {
                    e.preventDefault();
                    const touch = e.touches[0];
                    const DAMPING = 0.2; // 20% of finger movement for fine control
                    const deltaX = (touch.clientX - lastTouchPos.current.x) * DAMPING;
                    const deltaY = (touch.clientY - lastTouchPos.current.y) * DAMPING;
                    lastTouchPos.current = { x: touch.clientX, y: touch.clientY };
                    setTextElements(prev => prev.map(text =>
                      text.id === draggingTextId
                        ? { ...text, x: text.x + deltaX, y: text.y + deltaY }
                        : text
                    ));
                  }
                  // Handle image dragging on mobile
                  if (draggingImageId && (currentTool === 'image' || currentTool === 'signature')) {
                    e.preventDefault();
                    const touch = e.touches[0];
                    const DAMPING = 0.2;
                    const deltaX = (touch.clientX - lastTouchPos.current.x) * DAMPING;
                    const deltaY = (touch.clientY - lastTouchPos.current.y) * DAMPING;
                    lastTouchPos.current = { x: touch.clientX, y: touch.clientY };
                    setImageElements(prev => prev.map(img =>
                      img.id === draggingImageId
                        ? { ...img, x: img.x + deltaX, y: img.y + deltaY }
                        : img
                    ));
                  }
                }}
              >
                <img
                  ref={(el) => { pageImgRefs.current[page.page_number] = el; }}
                  src={page.thumbnail}
                  alt={`Page ${page.page_number}`}
                  className="w-full h-auto pointer-events-none select-none"
                  style={{ 
                    maxWidth: `${1200 * zoom}px`,
                    display: 'block',
                  }}
                  draggable={false}
                />
                
                {/* Text elements overlay */}
                {textElements
                  .filter(text => text.pageNumber === page.page_number)
                  .map((text) => {
                    // Compute display font size: if pdfFontSize was edited, convert back to screen pixels
                    let displayFontSize = text.fontSize;
                    if (text.pdfFontSize) {
                      const pdfWidth = page.width || 612;
                      const imgEl = pageImgRefs.current[page.page_number];
                      if (imgEl && imgEl.clientWidth) {
                        displayFontSize = text.pdfFontSize / pdfWidth * imgEl.clientWidth;
                      }
                    }
                    return (
                    <div
                      key={text.id}
                      data-text-element="true"
                      data-testid={`text-element-${text.id}`}
                      onMouseDown={(e) => handleTextMouseDown(e, text.id)}
                      onTouchStart={(e) => handleTextTouchStart(e, text.id)}
                      className={`absolute cursor-move select-none ${
                        selectedTextId === text.id ? 'ring-2 ring-primary ring-offset-1 bg-primary/5' : 'hover:ring-1 hover:ring-primary/50'
                      }`}
                      style={{
                        left: `${text.x}px`,
                        top: `${text.y}px`,
                        fontSize: `${displayFontSize}px`,
                        color: text.color,
                        fontFamily: PDF_PREVIEW_FONT[(text.fontFamily || 'helvetica').toLowerCase()] || 'PDFHelvetica',
                        fontWeight: (text.fontFamily || '').includes('-bold') ? 'bold' : 'normal',
                        lineHeight: '1',
                        whiteSpace: 'nowrap',
                        touchAction: 'none',
                      }}
                    >
                      {text.text}
                    </div>
                    );
                  })}

                {/* Image elements overlay */}
                {imageElements
                  .filter(img => img.pageNumber === page.page_number)
                  .map((img) => (
                    <div
                      key={img.id}
                      data-image-element="true"
                      data-testid={`image-element-${img.id}`}
                      onMouseDown={(e) => handleImageMouseDown(e, img.id)}
                      onTouchStart={(e) => handleImageTouchStart(e, img.id)}
                      className={`absolute cursor-move select-none ${
                        selectedImageId === img.id ? 'ring-2 ring-primary ring-offset-1' : 'hover:ring-1 hover:ring-primary/50'
                      }`}
                      style={{
                        left: `${img.x}px`,
                        top: `${img.y}px`,
                        width: `${img.width}px`,
                        height: `${img.height}px`,
                        touchAction: 'none',
                      }}
                    >
                      <img
                        src={img.data}
                        alt="Placed"
                        className="w-full h-full object-contain pointer-events-none"
                        draggable={false}
                      />
                      {selectedImageId === img.id && (
                        <div
                          data-testid={`image-resize-handle-${img.id}`}
                          data-image-element="true"
                          onMouseDown={(e) => handleResizeMouseDown(e, img.id)}
                          onTouchStart={(e) => handleResizeTouchStart(e, img.id)}
                          className="absolute w-4 h-4 bg-primary border-2 border-white rounded-sm shadow"
                          style={{ right: '-8px', bottom: '-8px', cursor: 'nwse-resize', touchAction: 'none' }}
                        />
                      )}
                    </div>
                  ))}
                
                {/* Pending highlight overlays (removable before save) */}
                {highlightElements
                  .filter(hl => hl.pageNumber === page.page_number)
                  .map((hl) => (
                    <div
                      key={hl.id}
                      data-testid={`highlight-element-${hl.id}`}
                      className="absolute"
                      style={{
                        left: `${hl.x}px`,
                        top: `${hl.y}px`,
                        width: `${hl.width}px`,
                        height: `${hl.height}px`,
                        pointerEvents: 'none',
                      }}
                    >
                      <div className="w-full h-full" style={{ backgroundColor: hl.color, opacity: hl.opacity }} />
                      <button
                        data-testid={`remove-highlight-${hl.id}`}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setHighlightElements(prev => prev.filter(h => h.id !== hl.id));
                          toast.success('Highlight removed');
                        }}
                        className="absolute w-5 h-5 rounded-full bg-red-500 text-white text-xs leading-none flex items-center justify-center shadow"
                        style={{ top: '-10px', right: '-10px', pointerEvents: 'auto', cursor: 'pointer' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}

                {/* Erase/Highlight selection rectangle */}
                {eraseRect && eraseRect.pageNumber === page.page_number && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${eraseRect.x}px`,
                      top: `${eraseRect.y}px`,
                      width: `${eraseRect.width}px`,
                      height: `${eraseRect.height}px`,
                      border: currentTool === 'highlight' ? `2px dashed ${highlightConfig.color}` : '2px dashed #EF4444',
                      backgroundColor: currentTool === 'highlight' ? highlightConfig.color : 'rgba(239, 68, 68, 0.1)',
                      opacity: currentTool === 'highlight' ? highlightConfig.opacity : 1,
                      pointerEvents: 'none',
                    }}
                  />
                )}

                {/* Tool hints */}
                {currentTool && (
                  <div className="absolute bottom-3 left-3 glass px-3 py-1.5 rounded text-xs text-foreground font-medium shadow-lg flex items-center gap-2">
                    {currentTool === 'text' && pendingText && (
                      <>
                        <span>✏️</span>
                        <span>Click to place text</span>
                      </>
                    )}
                    {currentTool === 'text' && !pendingText && (
                      <>
                        <span>📝</span>
                        <span>Type text in sidebar first</span>
                      </>
                    )}
                    {currentTool === 'erase' && (
                      <>
                        <span>🗑️</span>
                        <span>Drag to select area</span>
                      </>
                    )}
                    {currentTool === 'highlight' && (
                      <>
                        <span>🖊️</span>
                        <span>Drag to highlight area</span>
                      </>
                    )}
                    {(currentTool === 'image' || currentTool === 'signature') && pendingImage && (
                      <>
                        <span>🖼️</span>
                        <span>Click to place image</span>
                      </>
                    )}
                    {currentTool === 'image' && !pendingImage && (
                      <>
                        <span>🖼️</span>
                        <span>Upload image in sidebar first</span>
                      </>
                    )}
                  </div>
                )}
                
                {/* Size indicator when dragging */}
                {isDragging && eraseRect && eraseRect.pageNumber === page.page_number && (
                  <div className="absolute top-3 right-3 glass px-2 py-1 rounded text-xs text-foreground font-mono">
                    {Math.round(eraseRect.width)} × {Math.round(eraseRect.height)} px
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default MainCanvas;